// PLAN-cockpit-v2-iter1 Phase 2: V2 内部 routing (Curate 廃止、順序変更)
//
// /v2/         → home
// /v2/reflect  → reflect (2 番目に昇格、#4 Lens 順位変更)
// /v2/explore  → explore
// /v2/collab   → collaborate
// /v2/graph    → graph
// /v2/curate   → home redirect (Curate Lens 廃止、機能は Detail edit mode に統合)

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

// Curate は型から削除 (廃止。旧 '/v2/curate' は home にリダイレクト)
// domain Lenses: finance / health / mind (PLAN-cockpit-v2-domain-lenses Phase 1)
export type LensId = 'home' | 'reflect' | 'explore' | 'judgment' | 'finance' | 'health' | 'mind' | 'collaborate' | 'graph'

// PLAN-judgment-cockpit (2026-05-15): judgment Lens を tools グループに追加 (8 番目、Cmd+8)
// Cmd+0=Home, +1=Reflect, +2=Explore, +3=Finance, +4=Health, +5=Mind, +6=Collab, +7=Graph, +8=Judgment
export const LENS_ORDER: LensId[] = ['home', 'reflect', 'explore', 'finance', 'health', 'mind', 'collaborate', 'graph', 'judgment']

const PATH_TO_LENS: Record<string, LensId> = {
  '/v2': 'home',
  '/v2/': 'home',
  '/v2/reflect': 'reflect',
  '/v2/explore': 'explore',
  '/v2/curate': 'home',       // 廃止 → home redirect
  '/v2/judgment': 'judgment', // PLAN-judgment-cockpit (2026-05-15)
  '/v2/finance': 'finance',
  '/v2/health': 'health',
  '/v2/mind': 'mind',
  '/v2/collab': 'collaborate',
  '/v2/collaborate': 'collaborate',
  '/v2/graph': 'graph',
}

const LENS_TO_PATH: Record<LensId, string> = {
  home: '/v2/',
  reflect: '/v2/reflect',
  explore: '/v2/explore',
  judgment: '/v2/judgment',
  finance: '/v2/finance',
  health: '/v2/health',
  mind: '/v2/mind',
  collaborate: '/v2/collab',
  graph: '/v2/graph',
}

// PLAN-permalink-cockpit-convergence Phase C: route state を discriminated union 化。
// /v/:id permalink は lens とは別の route。
export type RouteState =
  | { kind: 'lens'; lens: LensId }
  | { kind: 'permalink'; id: string }

function readRoute(): RouteState {
  const path = window.location.pathname
  const pm = path.match(/^\/v\/([^/]+)\/?$/)
  if (pm) return { kind: 'permalink', id: decodeURIComponent(pm[1]) }
  if (path === '/v2/' || path === '/v2') return { kind: 'lens', lens: 'home' }
  return { kind: 'lens', lens: PATH_TO_LENS[path] ?? 'home' }
}

interface RouterCtx {
  route: RouteState
  /** route.kind==='lens' なら route.lens、permalink のときは 'home' (互換 default) */
  lens: LensId
  navigate: (lens: LensId) => void
  navigatePermalink: (id: string) => void
  pathFor: (lens: LensId) => string
}

const Ctx = createContext<RouterCtx | null>(null)

export function V2RouterProvider({ children }: { children: React.ReactNode }) {
  const [route, setRoute] = useState<RouteState>(readRoute)

  useEffect(() => {
    const onPop = () => setRoute(readRoute())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const navigate = useCallback((next: LensId) => {
    window.history.pushState(null, '', LENS_TO_PATH[next])
    setRoute({ kind: 'lens', lens: next })
  }, [])

  const navigatePermalink = useCallback((id: string) => {
    window.history.pushState(null, '', `/v/${encodeURIComponent(id)}`)
    setRoute({ kind: 'permalink', id })
  }, [])

  const pathFor = useCallback((l: LensId) => LENS_TO_PATH[l], [])

  const lens: LensId = route.kind === 'lens' ? route.lens : 'home'

  const value = useMemo(
    () => ({ route, lens, navigate, navigatePermalink, pathFor }),
    [route, lens, navigate, navigatePermalink, pathFor],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useV2Router(): RouterCtx {
  const v = useContext(Ctx)
  if (!v) throw new Error('useV2Router must be used within V2RouterProvider')
  return v
}
